import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiFacebook, FiLinkedin, FiTwitter } from 'react-icons/fi';
import '../styles/Footer.css';

const Footer = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <motion.div
        className="footer-content"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="footer-grid">
            {/* Brand Section */}
            <motion.div className="footer-section" variants={itemVariants}>
              <h3>Active Care Residential</h3>
              <p className="footer-description">
                Safe, nurturing residential accommodation for vulnerable adults with UK regulatory compliance.
              </p>
              <div className="social-links">
                {[
                  { icon: FiFacebook, label: 'Facebook' },
                  { icon: FiLinkedin, label: 'LinkedIn' },
                  { icon: FiTwitter, label: 'Twitter' }
                ].map((social, i) => (
                  <motion.a
                    key={i}
                    href="#"
                    className="social-link"
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    title={social.label}
                  >
                    <social.icon />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div className="footer-section" variants={itemVariants}>
              <h4>Quick Links</h4>
              <nav className="footer-nav">
                {[
                  { to: '/', label: 'Home' },
                  { to: '/about', label: 'About' },
                  { to: '/services', label: 'Services' },
                  { to: '/locations', label: 'Locations' }
                ].map((link, i) => (
                  <motion.div key={i} whileHover={{ x: 5 }}>
                    <Link to={link.to}>{link.label}</Link>
                  </motion.div>
                ))}
              </nav>
            </motion.div>

            {/* Services Links */}
            <motion.div className="footer-section" variants={itemVariants}>
              <h4>Services</h4>
              <nav className="footer-nav">
                {[
                  { to: '/team', label: 'Our Team' },
                  { to: '/contact', label: 'Contact' },
                  { to: '/faqs', label: 'FAQs' },
                  { to: '/partners', label: 'Partners' }
                ].map((link, i) => (
                  <motion.div key={i} whileHover={{ x: 5 }}>
                    <Link to={link.to}>{link.label}</Link>
                  </motion.div>
                ))}
              </nav>
            </motion.div>

            {/* Contact Info */}
            <motion.div className="footer-section" variants={itemVariants}>
              <h4>Contact Info</h4>
              <div className="contact-info">
                <div className="contact-item">
                  <FiMapPin /> East Ham, London
                </div>
                <div className="contact-item">
                  <FiPhone /> +44 7737 207521
                </div>
                <div className="contact-item">
                  <FiMail /> info@activecareresidential.co.uk<br/>
                </div>
                <div className="contact-item">
                  <FiMail /> sanayatt1@hotmail.com
                </div>
                <div className="contact-item">
                  <FiPhone /> 24/7 Emergency Support
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Compliance & Legal */}
      <motion.div
        className="footer-compliance"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="container">
          <div className="compliance-content">
            <div className="compliance-left">
              <p className="compliance-text">
                UK Regulated Care Services | Safeguarding Compliant
              </p>
            </div>

            <div className="compliance-links">
              <motion.div whileHover={{ textDecoration: 'underline' }}>
                <Link to="/privacy-policy">Privacy Policy</Link>
              </motion.div>
              <span className="divider">•</span>
              <motion.div whileHover={{ textDecoration: 'underline' }}>
                <Link to="/terms-of-service">Terms of Service</Link>
              </motion.div>
              <span className="divider">•</span>
              <motion.div whileHover={{ textDecoration: 'underline' }}>
                <Link to="/gdpr-information">GDPR Information</Link>
              </motion.div>
              <span className="divider">•</span>
              <motion.div whileHover={{ textDecoration: 'underline' }}>
                <Link to="/cookie-policy">Cookie Policy</Link>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Copyright */}
      <motion.div
        className="footer-bottom"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="container">
          <p>&copy; {currentYear} Active Care Residential. All rights reserved. Registration: 14464980</p>
          <p className="footer-credit">
            Built with care and compliance | GDPR Compliant | UK Regulatory Standards
          </p>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;